import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useParams } from 'react-router-dom';

// Styles
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { FormCard } from 'js/components/Configurations/CreateClub/styles';

// Components
import { DrawItems } from './DrawItems';
import { ModalIncompleteItem } from './ModalIncompleteItem';
import { PageStructure } from 'js/components/TriiboComponents/PageStructure/PageStructure';
import { StandardSelect } from 'js/components/TriiboComponents/Selects/StandardSelect';

// Apis
import { createRaffleConfiguration } from 'js/library/utils/API/Draws/createRaffleConfiguration';
import { findStoreByProximity } from 'js/library/utils/API/CampaignLandinPage/apiFindStoreByProximity';
import { getByOwner } from 'js/library/utils/API/Campaigns/apiGetByOwner';
import { getVoucherTemplate } from 'js/library/utils/API/getVoucherTemplate';
import { updateRaffle } from 'js/library/utils/API/Draws/updateRaffle';

const drawTitle = {
  criar: 'Criar sorteio',
  editar: 'Editar sorteio',
  sorteio: 'Sortear/Ver sorteio',
};

export const ActionsDraw = () => {
  const { id } = useParams();
  const { pathname } = window.location;
  const location = useLocation();
  const drawSettings = location?.state?.drawSettings || null;
  const partnerId = localStorage.getItem('partnerIdClube');
  const type = pathname.split('/')[5];
  const isNotRaffle = type !== 'sorteio';

  const [campaigns, setCampaigns] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawData, setDrawData] = useState(
    drawSettings ?? {
      campaignId: '',
      raffleName: '',
      partnerId: partnerId,
      periodId: 0,
      raffleQuantity: 0,
      voucherTemplateIdRaffle: '',

      raffleVouchers: [
        {
          description: '',
          store: '',
        },
      ],
    }
  );
  const [inputSearch, setInputSearch] = useState('');
  const [periods, setPeriods] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [promotionSelected, setPromotionSelected] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const [incompleteItems, setIncompleteItems] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    getByOwner(id)
      .then((campaigns) => {
        const sanitizeCampaigns = Object.entries(campaigns.result)
          .map(([key, campaign]) => ({
            id: key,
            ...campaign,
          }))
          .filter((campaign) => campaign.periods && campaign.periods.length > 0);

        setCampaigns(sanitizeCampaigns);

        if (drawSettings?.campaignId) {
          const selectedCampaign = sanitizeCampaigns.find((c) => c.id === drawSettings.campaignId);
          if (selectedCampaign && selectedCampaign.periods) {
            setPeriods(selectedCampaign.periods);
          }
        }
      })
      .catch((error) => {
        toast.error(error.data.error);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (inputSearch.length > 2) debouncedSearch(inputSearch);
  }, [inputSearch]);

  useEffect(() => {
    const length = drawData.raffleVouchers.length;

    setDrawData((prev) => ({
      ...prev,
      raffleQuantity: length,
    }));
  }, [drawData.raffleVouchers]);

  useEffect(() => {
    (async function fetchPromotionFromTemplate() {
      if (drawSettings) {
        setLoadingTemplate(true);
        try {
          const response = await getVoucherTemplate(drawSettings.voucherTemplateIdRaffle);
          const { result } = response;

          setPromotionSelected(result);
        } catch (err) {
          console.error('Erro ao buscar promoção para edição:', err);
        } finally {
          setLoadingTemplate(false);
        }
      }
    })();
  }, [drawSettings?.voucherTemplateIdRaffle]);

  const dataStore = async (input) => {
    setLoadingTemplate(true);
    try {
      const result = await findStoreByProximity(input, true, 0, 10);
      const { response } = result;

      const filteredPromotions = response.filter((promotion) => promotion.templateId);
      setPromotions(filteredPromotions);
    } catch (error) {
      console.error('Erro ao obter a lista de promoções:', error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const debounce = (func, delay) => {
    let timer;
    return function (arg) {
      clearTimeout(timer);
      timer = setTimeout(() => func.call(this, arg), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((input) => {
      dataStore(input);
    }, 500),
    []
  );

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCampaignChange = (value) => {
    const selectedCampaign = campaigns.filter((campaign) => campaign.id === value)[0];

    setDrawData((prevState) => ({
      ...prevState,
      campaignId: value,
      periodId: 0,
    }));

    if (selectedCampaign && selectedCampaign.periods) {
      setPeriods(selectedCampaign.periods);
    } else {
      setPeriods([]);
    }
  };

  const handleConfirmIncomplete = () => {
    const newRaffleVouchers = [...drawData.raffleVouchers].filter(
      (_, i) => !incompleteItems.includes(i)
    );

    const updatedDrawData = {
      ...drawData,
      raffleVouchers: newRaffleVouchers,
    };

    setDrawData(updatedDrawData);
    setShowConfirmModal(false);
    createOrEditDraw(true, updatedDrawData);
  };

  const createOrEditDraw = async (corrected, data = drawData) => {
    setLoading(true);
    try {
      if (!corrected) {
        const incomplete = data.raffleVouchers
          .map((val, index) => (!val.description || !val.store ? index : null))
          .filter((i) => i !== null);

        if (incomplete.length > 0) {
          setIncompleteItems(incomplete);
          setShowConfirmModal(true);
          return;
        }
      }

      if (data.campaignId === '') {
        return toast.error('Selecione uma campanha!');
      } else if (data.periodId === 0) {
        return toast.error('Selecione um período!');
      } else if (data.voucherTemplateIdRaffle === '') {
        return toast.error('Selecione a promoção do sorteio!');
      } else if (data.raffleName === '') {
        return toast.error('Informe um nome para o sorteio antes de prosseguir.');
      }

      if (drawSettings?.hasUsersList) {
        const { id, raffleQuantity, raffleVouchers } = data;
        await updateRaffle({ raffleQuantity, raffleVouchers, raffleIdentifier: id });
      } else {
        if (!!drawSettings) {
          const {
            approvedDrawsCounter,
            creationDate,
            drawnList,
            hasUsersList,
            id,
            updateDate,
            ...rest
          } = data;
          await updateRaffle({ ...rest, raffleIdentifier: id });
        } else {
          await createRaffleConfiguration(data);
        }
      }

      drawSettings
        ? toast.success('Sorteio editado com sucesso!')
        : toast.success('Sorteio criado com sucesso!');
      window.history.back();
    } catch (error) {
      console.log('Erro ao criar sorteio:', error);
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageStructure title={drawTitle[type]}>
      <FormCard
        style={{
          width: '100%',
          margin: '0 auto',
          maxWidth: '100%',
          height: 'auto',
        }}
      >
        <Grid container spacing={1}>
          {isNotRaffle && (
            <>
              <Grid display={'flex'} gap={2} item md={12} xs={12}>
                <TextField
                  fullWidth
                  label="Nome do sorteio*"
                  onChange={(event) =>
                    setDrawData((prevDrawData) => ({
                      ...prevDrawData,
                      raffleName: event.target.value,
                    }))
                  }
                  value={drawData.raffleName}
                />
              </Grid>
              <Grid display={'flex'} gap={2} item md={12} xs={12}>
                <StandardSelect
                  disabled={drawSettings?.hasUsersList}
                  displayProp="id"
                  id="id"
                  label="Campanha*"
                  menuOptions={campaigns}
                  setValue={handleCampaignChange}
                  value={drawData.campaignId}
                  width={'100%'}
                />

                <FormControl fullWidth>
                  <InputLabel id="period-select-label">Período</InputLabel>
                  <Select
                    disabled={!drawData.campaignId || drawSettings?.hasUsersList}
                    label="Período*"
                    labelId="period-select-label"
                    onChange={(event) =>
                      setDrawData((prevState) => ({
                        ...prevState,
                        periodId: event.target.value,
                      }))
                    }
                    value={drawData.periodId}
                  >
                    <MenuItem value={0} disabled>
                      Selecionar período
                    </MenuItem>
                    {periods.map((period) => (
                      <MenuItem key={period.id} value={period.id}>
                        {`${formatDateTime(period.startAt)} até ${formatDateTime(period.endsAt)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item md={12} xs={12}>
                <Autocomplete
                  disabled={drawSettings?.hasUsersList}
                  fullWidth
                  disablePortal
                  sx={{ mt: '15px', mb: '5px' }}
                  id="combo-box-demo"
                  options={promotions}
                  value={promotionSelected ?? null}
                  getOptionLabel={(option) =>
                    drawSettings
                      ? `Nome: ${option.titulo ?? option.title}`
                      : `Nome: ${option.title || ' Ainda não buscado'} | ID: ${
                          option.id || ' Sem ID'
                        } | TemplateId: ${option.templateId || ' Sem template'}`
                  }
                  blurOnSelect
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        loadingTemplate ? (
                          <>
                            <CircularProgress size={20} color="inherit" />
                            &nbsp;Pesquisando...
                          </>
                        ) : (
                          'Promoção do sorteio*'
                        )
                      }
                      placeholder="Pesquise"
                      disabled={loadingTemplate || drawSettings?.hasUsersList}
                      onChange={(event) => setInputSearch(event.target.value)}
                      onClick={() => setOptionsOpen(true)}
                      onFocus={() => setOptionsOpen(true)}
                      onBlur={() => setOptionsOpen(false)}
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: '200px' } }}
                  open={optionsOpen}
                  onClose={() => setOptionsOpen(false)}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setPromotionSelected(newValue);
                      setDrawData((prevDrawData) => ({
                        ...prevDrawData,
                        voucherTemplateIdRaffle: newValue.templateId,
                      }));
                    }
                  }}
                />
              </Grid>
            </>
          )}
          <DrawItems
            drawData={drawData}
            setDrawData={setDrawData}
            drawSettings={drawSettings}
            isNotRaffle={isNotRaffle}
          />
          <Box
            marginTop={'20px'}
            display={'flex'}
            alignItems={'center'}
            columnGap={'12px'}
            justifyContent={'center'}
            width={'100%'}
          >
            {isNotRaffle && (
              <Button
                color="primary"
                onClick={() => createOrEditDraw(false)}
                sx={{ minWidth: '12rem' }}
                variant="contained"
              >
                {loading ? (
                  <CircularProgress size={25} thickness={5} sx={{ color: '#fff' }} />
                ) : (
                  'Salvar'
                )}
              </Button>
            )}
          </Box>
          <ModalIncompleteItem
            open={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={handleConfirmIncomplete}
            items={incompleteItems}
          />
        </Grid>
      </FormCard>
    </PageStructure>
  );
};
